import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getUserProfile } from '../services/userService';
import { base64ToDataUrl } from '../utils/imageToBase64';
import Icon from './Icon';

function LeadDashboard({ leadData, imageUrl, onNewScan, onUpdate }) {
  const { user } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(leadData);
  const [selectedDraft, setSelectedDraft] = useState('formal');
  const [userProfile, setUserProfile] = useState(null);
  const [editingDraft, setEditingDraft] = useState(null); // Track which draft type is being edited (null, 'formal', 'casual', 'partnership')
  const [editedDrafts, setEditedDrafts] = useState({}); // Store edited draft content

  useEffect(() => {
    loadUserProfile();
  }, [user]);

  // Re-render when profile loads to update personalized drafts
  useEffect(() => {
    // This ensures drafts update when profile is loaded
  }, [userProfile]);

  const loadUserProfile = async () => {
    if (!user?.uid) return;
    try {
      const profile = await getUserProfile(user.uid);
      setUserProfile(profile);
    } catch (error) {
      console.warn('Could not load user profile:', error);
    }
  };

  // Helper function to remove duplicate closings
  const removeDuplicateClosings = (text, userName) => {
    if (!text) return text;
    
    // More comprehensive pattern to match closing signatures with optional name
    // Matches: "Best regards,\nFrank Zhao" or "Best regards,\n\nFrank Zhao" or just "Best regards,"
    const closingPattern = /(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]*/gi;
    
    // Find all closing matches
    const matches = [];
    let match;
    while ((match = closingPattern.exec(text)) !== null) {
      matches.push({
        text: match[0],
        index: match.index,
        fullMatch: match[0]
      });
    }
    
    // If we have multiple closings, keep only the last one
    if (matches.length > 1) {
      // Sort by index to process in order
      matches.sort((a, b) => a.index - b.index);
      
      // Keep the last closing, remove all others
      const lastMatch = matches[matches.length - 1];
      let cleaned = text;
      
      // Remove all closings except the last one (working backwards)
      for (let i = matches.length - 2; i >= 0; i--) {
        const matchToRemove = matches[i];
        const beforeMatch = cleaned.substring(0, matchToRemove.index).trimEnd();
        const afterMatch = cleaned.substring(matchToRemove.index + matchToRemove.text.length).trimStart();
        
        // If there's content after, check if we need to preserve spacing
        if (afterMatch && !afterMatch.startsWith('\n')) {
          cleaned = beforeMatch + '\n\n' + afterMatch;
        } else {
          cleaned = beforeMatch + afterMatch;
        }
      }
      
      return cleaned;
    }
    
    return text;
  };

  const getPersonalizedDraft = (draftText) => {
    if (!draftText) return '';
    
    // Get user's name from profile, fallback to email
    const userName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Follow Up AI';
    
    // Get convention name from lead data
    const conventionName = editedData?.convention || 'the event';
    
    // Create personalized introduction
    let intro = '';
    if (userProfile) {
      const parts = [];
      if (userProfile.name) parts.push(`I'm ${userProfile.name}`);
      if (userProfile.position && userProfile.company) {
        parts.push(`the ${userProfile.position} at ${userProfile.company}`);
      } else if (userProfile.position) {
        parts.push(`a ${userProfile.position}`);
      } else if (userProfile.company) {
        parts.push(`from ${userProfile.company}`);
      }
      if (parts.length > 0) {
        intro = parts.join(', ') + '. ';
      }
    }

    // Replace placeholders with actual name (handle multiple formats)
    let personalized = draftText;
    
    // Replace [Your Name] first (with brackets) - only replace standalone instances
    personalized = personalized.replace(/\[Your Name\]/gi, userName);
    // Then replace Your Name (without brackets) - but be careful not to replace it if it's part of a word
    // Only replace "Your Name" if it's at the end of a line (likely in closing signature)
    personalized = personalized.replace(/\bYour Name\b/gi, userName);
    // DO NOT replace event names - they should stay as the convention name
    // The GPT should already have the correct convention name in the draft
    
    // First, clean up any duplicate closings/signatures BEFORE other processing
    personalized = removeDuplicateClosings(personalized, userName);
    
    // Ensure greeting exists - if not, add one
    const hasGreeting = /^(Dear|Hi|Hello|Hey)\s+[^,\n]+[,]?\s*\n?/i.test(personalized.trim());
    if (!hasGreeting && editedData?.name) {
      // Add greeting if missing
      personalized = `Dear ${editedData.name},\n\n${personalized.trim()}`;
    }
    
    // Ensure closing exists - check more thoroughly
    const closingPattern = /(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]*$/i;
    const hasClosing = closingPattern.test(personalized.trim());
    
    if (!hasClosing) {
      // Add closing if missing
      personalized = personalized.trim() + `\n\nBest regards,\n${userName}`;
    } else {
      // Replace "Your Name" in closing if it exists
      personalized = personalized.replace(/(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*Your\s+Name$/i, `$1,\n${userName}`);
    }
    
    // Final cleanup: remove duplicates again after all replacements
    personalized = removeDuplicateClosings(personalized, userName);
    
    // Add personalized introduction after greeting (Dear/Hi/Hello [Name],) if we have profile info
    if (intro && !personalized.includes(intro)) {
      // Look for common greeting patterns: "Dear [Name],", "Hi [Name],", "Hello [Name],"
      // Case insensitive, with optional comma, handles both with and without newline
      const greetingPatterns = [
        /(Dear\s+[^,\n]+[,]?\s*\n?)/i,
        /(Hi\s+[^,\n]+[,]?\s*\n?)/i,
        /(Hello\s+[^,\n]+[,]?\s*\n?)/i,
        /(Hey\s+[^,\n]+[,]?\s*\n?)/i
      ];
      
      let match = null;
      for (const pattern of greetingPatterns) {
        match = personalized.match(pattern);
        if (match) break;
      }
      
      if (match) {
        // Insert intro right after the greeting
        const insertPosition = match.index + match[0].length;
        // Add newline if not already there, then intro
        const needsNewline = !match[0].endsWith('\n');
        personalized = personalized.slice(0, insertPosition) + 
                      (needsNewline ? '\n' : '') + intro + 
                      personalized.slice(insertPosition);
      } else {
        // If no greeting found, add at the beginning
        personalized = intro + personalized;
      }
    }
    
    // Final validation: ensure draft structure is correct
    personalized = validateDraftStructure(personalized, userName, editedData?.name);
    
    return personalized;
  };

  // Validation function to ensure draft structure is correct
  const validateDraftStructure = (draft, userName, contactName) => {
    if (!draft) return draft;
    
    let validated = draft.trim();
    
    // 1. Remove any duplicate closings (final cleanup) - do this first
    validated = removeDuplicateClosings(validated, userName);
    
    // 2. More aggressive duplicate removal - look for repeated closing patterns
    // Pattern: "Best regards,\nName\n\nBest regards,\nName" or similar
    const duplicateClosingPattern = /((Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]+\s*\n+\s*)(\2[,\s]*\n+\s*[^\n]+)/gi;
    let prevValidated = '';
    while (validated !== prevValidated) {
      prevValidated = validated;
      validated = validated.replace(duplicateClosingPattern, '$1');
    }
    
    // 3. Ensure only one closing exists at the end - more thorough check
    const closingRegex = /(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]*/gi;
    const allClosings = [];
    let match;
    while ((match = closingRegex.exec(validated)) !== null) {
      allClosings.push({
        text: match[0],
        index: match.index
      });
    }
    
    if (allClosings.length > 1) {
      // Keep only the last closing
      const lastClosing = allClosings[allClosings.length - 1];
      const beforeLast = validated.substring(0, lastClosing.index).trimEnd();
      // Remove any other closing patterns from beforeLast
      const cleanedBefore = beforeLast.replace(/(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*[^\n]*/gi, '').trimEnd();
      validated = cleanedBefore + '\n\n' + lastClosing.text.trim();
    }
    
    // 4. Remove duplicate user names in closing (e.g., "Frank Zhao\n\nFrank Zhao")
    const duplicateNamePattern = new RegExp(`(${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})\\s*\\n+\\s*\\1`, 'gi');
    validated = validated.replace(duplicateNamePattern, userName);
    
    // 5. Ensure closing has the user's name (not placeholder)
    const hasClosing = /(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)/i.test(validated);
    if (hasClosing) {
      // Check if closing ends with user name, if not add it
      const closingWithName = new RegExp(`(Best\\s+regards|Best|Sincerely|Regards|Thanks|Thank\\s+you)[,\\s]*\\n+\\s*${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\s*$`, 'i');
      if (!closingWithName.test(validated)) {
        // Replace closing without name or with placeholder
        validated = validated.replace(/(Best\s+regards|Best|Sincerely|Regards|Thanks|Thank\s+you)[,\s]*\n+\s*([^\n]*)$/i, `$1,\n${userName}`);
      }
    }
    
    return validated.trim();
  };

  const handleInputChange = (field, value) => {
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = () => {
    // Merge edited drafts into editedData
    const updatedData = {
      ...editedData,
      drafts: {
        ...editedData.drafts,
        ...editedDrafts
      }
    };
    onUpdate(updatedData);
    setIsEditing(false);
    setEditingDraft(null);
    setEditedDrafts({});
  };

  const handleCancel = () => {
    setEditedData(leadData);
    setIsEditing(false);
    setEditingDraft(null);
    setEditedDrafts({});
  };

  const handleDraftEdit = (draftType) => {
    setEditingDraft(draftType);
    // Initialize edited draft if not already set
    if (!editedDrafts[draftType]) {
      setEditedDrafts(prev => ({
        ...prev,
        [draftType]: editedData.drafts?.[draftType] || ''
      }));
    }
  };

  const handleDraftChange = (draftType, value) => {
    setEditedDrafts(prev => ({
      ...prev,
      [draftType]: value
    }));
  };

  const handleDraftSave = (draftType) => {
    // Validate and clean the draft before saving
    const userName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Follow Up AI';
    let cleanedDraft = editedDrafts[draftType] || '';
    
    // Remove duplicate closings
    cleanedDraft = removeDuplicateClosings(cleanedDraft, userName);
    // Validate structure
    cleanedDraft = validateDraftStructure(cleanedDraft, userName, editedData?.name);
    
    // Update editedData with the cleaned draft
    setEditedData(prev => ({
      ...prev,
      drafts: {
        ...prev.drafts,
        [draftType]: cleanedDraft
      }
    }));
    // Also update editedDrafts to reflect the cleaned version
    setEditedDrafts(prev => ({
      ...prev,
      [draftType]: cleanedDraft
    }));
    setEditingDraft(null);
  };

  const handleDraftCancel = (draftType) => {
    // Remove from editedDrafts to revert
    setEditedDrafts(prev => {
      const newDrafts = { ...prev };
      delete newDrafts[draftType];
      return newDrafts;
    });
    setEditingDraft(null);
  };

  const handleSendEmail = () => {
    const email = editedData.email;
    if (!email) {
      alert('Email address is required to send email');
      return;
    }

    // Get user's name from profile, fallback to email
    const userName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Follow Up AI';
    
    // Create personalized introduction
    let intro = '';
    if (userProfile) {
      const parts = [];
      if (userProfile.name) parts.push(`I'm ${userProfile.name}`);
      if (userProfile.position && userProfile.company) {
        parts.push(`the ${userProfile.position} at ${userProfile.company}`);
      } else if (userProfile.position) {
        parts.push(`a ${userProfile.position}`);
      } else if (userProfile.company) {
        parts.push(`from ${userProfile.company}`);
      }
      if (parts.length > 0) {
        intro = parts.join(', ') + '. ';
      }
    }

    // Get the selected draft
    let draftText = editedData.drafts?.[selectedDraft] || '';
    
    // Replace placeholder with actual name (handle both [Your Name] and Your Name)
    // DO NOT replace event/convention names - they should remain as the actual event name
    draftText = draftText.replace(/\[Your Name\]/gi, userName);
    draftText = draftText.replace(/\bYour Name\b/gi, userName);
    
    // Add personalized introduction at the beginning if we have profile info
    if (intro && !draftText.includes(intro)) {
      draftText = intro + draftText;
    }

    const conventionName = editedData.convention || 'the event';
    const subject = encodeURIComponent(`Great meeting you at ${conventionName} - ${userName}`);
    const body = encodeURIComponent(draftText);
    
    window.location.href = `mailto:${email}?subject=${subject}&body=${body}`;
  };

  const handleOpenLinkedIn = () => {
    // Priority 1: Direct LinkedIn profile URL if available
    if (editedData.linkedin_url) {
      window.open(editedData.linkedin_url, '_blank');
    } 
    // Priority 2: Google search query as fallback
    else if (editedData.linkedin_search_query) {
      window.open(editedData.linkedin_search_query, '_blank');
    } 
    // Priority 3: Generate Google search from name and company
    else {
      const query = encodeURIComponent(`${editedData.name || ''} ${editedData.company || ''} LinkedIn`.trim());
      window.open(`https://www.google.com/search?q=${query}`, '_blank');
    }
  };

  const fields = [
    { key: 'name', label: 'Name', icon: '👤' },
    { key: 'title', label: 'Title', icon: '💼' },
    { key: 'company', label: 'Company', icon: '🏢' },
    { key: 'email', label: 'Email', icon: '📧' },
    { key: 'phone', label: 'Phone (all numbers)', icon: '📞' },
    { key: 'address', label: 'Address', icon: '📍' },
    { key: 'website', label: 'Website', icon: '🌐' },
    { key: 'linkedin_url', label: 'LinkedIn URL', icon: '💼' },
    { key: 'convention', label: 'Convention/Event', icon: '🎪' },
    { key: 'industry_guess', label: 'Industry', icon: '🏭' },
  ];

  return (
    <div className="mt-6 space-y-6">
      {/* Lead Information - Show first so users know processing is complete */}
      <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 space-y-6 border border-zinc-200 dark:border-zinc-800">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-bold text-zinc-900 dark:text-zinc-100">Lead Information</h2>
          <div className="flex gap-2">
            {!isEditing ? (
              <button
                onClick={() => setIsEditing(true)}
                className="px-4 py-2 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg text-sm font-medium transition-colors"
              >
                <Icon emoji="✏️" size="text-base" className="mr-1" /> Edit
              </button>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors flex items-center"
                >
                  <Icon emoji="✅" size="text-base" className="mr-1" /> Save
                </button>
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg text-sm font-medium transition-colors flex items-center"
                >
                  <Icon emoji="❌" size="text-base" className="mr-1" /> Cancel
                </button>
              </>
            )}
          </div>
        </div>

        <div className="space-y-4">
          {fields.map(field => (
            <div key={field.key}>
              <label className="block text-sm font-medium text-zinc-600 dark:text-zinc-400 mb-1.5 flex items-center gap-2">
                <Icon emoji={field.icon} size="text-base" />
                {field.label}
              </label>
              {isEditing ? (
                field.key === 'address' ? (
                  <textarea
                    value={editedData[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-600 focus:border-transparent min-h-[80px] resize-y"
                    placeholder="Street, City, State, Zip, Country"
                  />
                ) : (
                  <input
                    type="text"
                    value={editedData[field.key] || ''}
                    onChange={(e) => handleInputChange(field.key, e.target.value)}
                    className="w-full px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-600 focus:border-transparent"
                    placeholder={field.key === 'phone' ? 'Office: +1-555-1234, Cell: +1-555-5678' : `Enter ${field.label.toLowerCase()}`}
                  />
                )
              ) : (
                <div className={`px-4 py-2.5 bg-zinc-50 dark:bg-zinc-800 rounded-lg text-zinc-900 dark:text-zinc-100 ${field.key === 'address' ? 'min-h-[60px]' : 'min-h-[42px]'} flex items-center`}>
                  {editedData[field.key] || <span className="text-zinc-500 dark:text-zinc-500 italic">Not found</span>}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Icebreaker */}
      {editedData.icebreaker && (
        <div className="bg-gradient-to-r from-zinc-50 to-zinc-100 dark:from-zinc-900 dark:to-zinc-800 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-700">
          <h3 className="text-sm font-semibold text-zinc-600 dark:text-zinc-400 mb-2 flex items-center gap-2">
            <Icon emoji="💡" size="text-base" /> Icebreaker
          </h3>
          <p className="text-zinc-700 dark:text-zinc-200">{editedData.icebreaker}</p>
        </div>
      )}

      {/* Email Drafts */}
      {editedData.drafts && Object.keys(editedData.drafts).length > 0 && (
        <div className="bg-white dark:bg-zinc-900 rounded-2xl p-6 space-y-4 border border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
              <Icon emoji="📧" size="text-lg" /> Follow-up Drafts
            </h3>
            {!isEditing && (
              <button
                onClick={() => setIsEditing(true)}
                className="px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg text-xs font-medium transition-colors flex items-center"
              >
                <Icon emoji="✏️" size="text-xs" className="mr-1" /> Edit Drafts
              </button>
            )}
          </div>
          
          {/* Draft Selector */}
          <div className="flex gap-2 overflow-x-auto pb-2">
            {Object.keys(editedData.drafts).map((type) => (
              <button
                key={type}
                onClick={() => setSelectedDraft(type)}
                className={`px-4 py-2 rounded-lg font-medium text-sm whitespace-nowrap transition-colors ${
                  selectedDraft === type
                    ? 'bg-zinc-200 dark:bg-zinc-700 text-zinc-900 dark:text-zinc-100'
                    : 'bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
                }`}
              >
                {type.charAt(0).toUpperCase() + type.slice(1)}
              </button>
            ))}
          </div>

          {/* Draft Content - Edit or View Mode */}
          {Object.keys(editedData.drafts).map((type) => {
            if (selectedDraft !== type) return null;
            
            const currentDraft = editedDrafts[type] !== undefined ? editedDrafts[type] : editedData.drafts[type];
            const isDraftEditing = editingDraft === type;
            
            return (
              <div key={type} className="space-y-2">
                <div className="flex items-center justify-between">
                  <span className="text-xs text-zinc-600 dark:text-zinc-400 font-medium">
                    {type.charAt(0).toUpperCase() + type.slice(1)} Draft
                  </span>
                  {isEditing && !isDraftEditing && (
                    <button
                      onClick={() => handleDraftEdit(type)}
                      className="px-2 py-1 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded text-xs transition-colors"
                    >
                      <Icon emoji="✏️" size="text-xs" className="mr-1" /> Edit
                    </button>
                  )}
                </div>
                
                {isDraftEditing ? (
                  <div className="space-y-2">
                    <textarea
                      value={editedDrafts[type] || ''}
                      onChange={(e) => handleDraftChange(type, e.target.value)}
                      className="w-full px-4 py-3 bg-zinc-50 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-700 rounded-lg text-zinc-900 dark:text-zinc-100 placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-zinc-600 focus:border-transparent min-h-[150px] resize-y text-sm leading-relaxed"
                      placeholder="Enter your follow-up message..."
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleDraftSave(type)}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-xs font-medium transition-colors flex items-center"
                      >
                        <Icon emoji="✅" size="text-xs" className="mr-1" /> Save
                      </button>
                      <button
                        onClick={() => handleDraftCancel(type)}
                        className="px-3 py-1.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-lg text-xs font-medium transition-colors flex items-center"
                      >
                        <Icon emoji="❌" size="text-xs" className="mr-1" /> Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-zinc-50 dark:bg-zinc-800 rounded-lg p-4 min-h-[120px]">
                    <p className="text-zinc-700 dark:text-zinc-300 whitespace-pre-wrap text-sm leading-relaxed">
                      {(() => {
                        const draft = getPersonalizedDraft(currentDraft);
                        // Apply validation when displaying
                        const userName = userProfile?.name || user?.displayName || user?.email?.split('@')[0] || 'Follow Up AI';
                        return validateDraftStructure(draft, userName, editedData?.name) || 'No draft available';
                      })()}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Image Preview - Show after processed info so users know processing is complete */}
      {(imageUrl || leadData?.thumbnailBase64) && (
        <div className="space-y-3">
          <h3 className="text-sm font-semibold text-zinc-600 dark:text-zinc-400 mb-2 flex items-center gap-2">
            <Icon emoji="📷" size="text-base" /> Business Card Images
          </h3>
          <div className="bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800">
            <p className="text-xs text-zinc-600 dark:text-zinc-400 p-2 bg-zinc-100 dark:bg-zinc-800">Front Side</p>
            <img
              src={imageUrl || base64ToDataUrl(leadData.thumbnailBase64)}
              alt="Business card front"
              className="w-full h-auto"
            />
          </div>
          {leadData?.thumbnailBackBase64 && (
            <div className="bg-white dark:bg-zinc-900 rounded-2xl overflow-hidden border border-zinc-200 dark:border-zinc-800">
              <p className="text-xs text-zinc-600 dark:text-zinc-400 p-2 bg-zinc-100 dark:bg-zinc-800">Back Side</p>
              <img
                src={base64ToDataUrl(leadData.thumbnailBackBase64)}
                alt="Business card back"
                className="w-full h-auto"
              />
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      <div className="space-y-3 sticky bottom-0 pb-4 bg-white dark:bg-zinc-950 -mx-4 px-4 pt-4">
        {editedData.email && (
          <button
            onClick={handleSendEmail}
            className="w-full py-4 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white rounded-xl font-semibold text-lg transition-all active:scale-98 flex items-center justify-center gap-2 shadow-lg shadow-blue-900/20"
          >
            <Icon emoji="📧" size="text-lg" />
            <span>Send Email</span>
          </button>
        )}
        
        <button
          onClick={handleOpenLinkedIn}
          className="w-full py-3 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
        >
          <Icon emoji="🔗" size="text-base" />
          <span>{editedData.linkedin_url ? 'Open LinkedIn Profile' : 'Search LinkedIn'}</span>
        </button>

        <button
          onClick={onNewScan}
          className="w-full py-3 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-100 rounded-xl font-medium transition-colors"
        >
          Scan Another Card
        </button>
      </div>
    </div>
  );
}

export default LeadDashboard;

